import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, ChevronLeft, ChevronRight, Maximize2, Minimize2,
  Heart, Users, Stethoscope, MapPin, Clock, DollarSign,
  CheckCircle, Calendar, FileText, Pill, Smartphone, Globe,
  Activity, Award, TrendingUp, Building2, Wifi,
  Video, MessageCircle, Search, Star, ShieldCheck, Sparkles,
} from 'lucide-react';

const PHONE_IMAGES = [
  'https://customer-assets.emergentagent.com/job_04ee5bbb-cdef-447a-872c-f5b562f9c74d/artifacts/2m7oyce4_gp_phone_08.png',
  'https://customer-assets.emergentagent.com/job_04ee5bbb-cdef-447a-872c-f5b562f9c74d/artifacts/l7vgttdv_gp_phone_05.png',
  'https://customer-assets.emergentagent.com/job_04ee5bbb-cdef-447a-872c-f5b562f9c74d/artifacts/b4s7azxl_gp_phone_07.png',
  'https://customer-assets.emergentagent.com/job_04ee5bbb-cdef-447a-872c-f5b562f9c74d/artifacts/kia9evmp_gp_phone_03.png',
];

const TEAL = '#0d9488';
const TEAL_DARK = '#0f766e';
const BG_DARK = '#0a1f1d';

/* ──────────────── Slide building blocks ──────────────── */
const Phone = ({ src, alt = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="rounded-[2rem] overflow-hidden shadow-2xl bg-black"
    style={{ aspectRatio: '9 / 19', maxHeight: '70vh', border: '8px solid #1a1a1a' }}
  >
    <img src={src} alt={alt} className="w-full h-full object-cover" />
  </motion.div>
);

const SlideShell = ({ children, badge, title, subtitle, theme = 'light', noPadding = false }) => {
  const isDark = theme === 'dark';
  return (
    <div className={`w-full h-full flex flex-col ${noPadding ? '' : 'p-8 md:p-12'} overflow-y-auto`} style={{ background: isDark ? BG_DARK : '#f8fafc', color: isDark ? 'white' : '#0f172a' }}>
      {(badge || title) && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 md:mb-8">
          {badge && (
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3" style={{ background: isDark ? 'rgba(13,148,136,0.25)' : '#ccfbf1', color: isDark ? '#5eead4' : TEAL_DARK }}>
              <Sparkles size={11} /> {badge}
            </div>
          )}
          {title && <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: isDark ? 'white' : '#0f172a' }}>{title}</h2>}
          {subtitle && <p className="text-base md:text-lg mt-2" style={{ color: isDark ? '#94a3b8' : '#475569' }}>{subtitle}</p>}
        </motion.div>
      )}
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
};

const BulletCard = ({ icon: Icon, title, body, accent = TEAL, dark = false }) => (
  <motion.div whileHover={{ y: -3 }} className="p-5 rounded-2xl flex items-start gap-3" style={{ background: dark ? 'rgba(255,255,255,0.05)' : 'white', border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}` }}>
    <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}20` }}><Icon size={18} style={{ color: accent }} /></span>
    <div>
      <div className="text-sm font-bold mb-0.5" style={{ color: dark ? 'white' : '#0f172a' }}>{title}</div>
      {body && <div className="text-xs leading-relaxed" style={{ color: dark ? '#94a3b8' : '#64748b' }}>{body}</div>}
    </div>
  </motion.div>
);

/* ──────────────── 12 Slides ──────────────── */
const slides = [
  // 1 — Intro
  {
    key: 'intro',
    title: 'Care365',
    render: () => (
      <SlideShell theme="dark" noPadding>
        <div className="w-full h-full flex items-center justify-center p-8 md:p-12 relative overflow-hidden" style={{ background: `radial-gradient(circle at 20% 20%, ${TEAL}40 0%, ${BG_DARK} 60%)` }}>
          <div className="absolute inset-0 opacity-30" style={{ background: `radial-gradient(circle at 80% 80%, ${TEAL}30 0%, transparent 50%)` }} />
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="text-center relative z-10 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6" style={{ background: 'rgba(13,148,136,0.25)', color: '#5eead4' }}>
              <Heart size={14} /> <span className="text-sm font-bold tracking-wider">DIGITAL HEALTHCARE FOR ZAMBIA</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-4 text-white">Care365</h1>
            <p className="text-xl md:text-2xl mb-8" style={{ color: '#94a3b8' }}>"Connecting patients, healthcare providers and institutions through one national telemedicine platform."</p>
            <div className="flex items-center justify-center gap-6 text-sm" style={{ color: '#cbd5e1' }}>
              <div className="flex items-center gap-2"><Building2 size={16} /> VoxIT / Innovage</div>
              <div className="w-1 h-1 rounded-full bg-white/40" />
              <div className="flex items-center gap-2"><Heart size={16} /> Healthcare Federation of Zambia</div>
            </div>
          </motion.div>
        </div>
      </SlideShell>
    ),
  },
  // 2 — Challenges
  {
    key: 'challenges', title: '01 · The Challenge',
    render: () => (
      <SlideShell badge="Healthcare in Zambia today" title="The challenges we are solving" subtitle="Care365 helps healthcare providers reach more patients without building more clinics.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { i: MapPin, t: 'Long distances to care', b: 'Patients travel hours to reach basic healthcare facilities.' },
            { i: Activity, t: 'Overloaded clinics', b: 'Limited capacity vs growing patient demand.' },
            { i: Stethoscope, t: 'Limited specialist access', b: 'Specialists concentrated in urban centres only.' },
            { i: DollarSign, t: 'High transport costs', b: 'Out-of-pocket spend on travel exceeds consultations.' },
            { i: Globe, t: 'Rural reach gap', b: 'Hard to deliver consistent care to remote communities.' },
            { i: FileText, t: 'Weak digital follow-up', b: 'Paper records and lost referrals between visits.' },
          ].map((c, i) => (<motion.div key={c.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}><BulletCard icon={c.i} title={c.t} body={c.b} accent={TEAL} /></motion.div>))}
        </div>
      </SlideShell>
    ),
  },
  // 3 — What is Care365
  {
    key: 'what', title: '02 · What is Care365?',
    render: () => (
      <SlideShell badge="Platform overview" title="One platform. Every step of care." subtitle="A mobile-first telemedicine platform purpose-built for the Zambian market.">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { i: Video, t: 'Video & audio consultations' },
              { i: MessageCircle, t: 'Real-time digital chat' },
              { i: Calendar, t: 'Appointment booking' },
              { i: Pill, t: 'Digital prescriptions' },
              { i: FileText, t: 'Patient records & history' },
              { i: Smartphone, t: 'Online + physical visits' },
              { i: DollarSign, t: 'Local payment integrations' },
              { i: Wifi, t: 'USSD for basic phones' },
            ].map((c, i) => (<motion.div key={c.t} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}><BulletCard icon={c.i} title={c.t} accent={TEAL} /></motion.div>))}
          </div>
          <div className="hidden lg:block"><Phone src={PHONE_IMAGES[0]} /></div>
        </div>
      </SlideShell>
    ),
  },
  // 4 — Patient Journey
  {
    key: 'journey', title: '03 · The Patient Journey',
    render: () => (
      <SlideShell badge="Onboarding flow" title="Designed for everyday Zambians" subtitle="A patient is consulting a healthcare provider in less than two minutes.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { src: PHONE_IMAGES[0], h: 'Welcome to Care365', s: 'Quick registration in under two minutes.' },
            { src: PHONE_IMAGES[1], h: 'Get Care Without the Queue', s: 'Skip the waiting room — book and consult instantly.' },
            { src: PHONE_IMAGES[2], h: 'Digital Healthcare 24/7', s: 'Doctors, nurses, psychologists and specialists.' },
          ].map((step, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} className="flex flex-col items-center text-center">
              <Phone src={step.src} delay={i * 0.15 + 0.1} />
              <h4 className="text-base font-bold mt-4" style={{ color: TEAL_DARK }}>{step.h}</h4>
              <p className="text-xs mt-1 max-w-xs" style={{ color: '#475569' }}>{step.s}</p>
            </motion.div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  // 5 — Find Right Specialist
  {
    key: 'specialist', title: '04 · Find the Right Specialist',
    render: () => (
      <SlideShell badge="Smart matching" title="Patients reach the right provider directly" subtitle="Search, filter and book — without referrals or long delays.">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 items-center">
          <Phone src={PHONE_IMAGES[1]} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { i: Search, t: 'Search by specialty', b: 'Find GPs, nurses, psychologists, dentists, specialists.' },
              { i: Activity, t: 'Smart filtering', b: 'By price, language, online status, rating, location.' },
              { i: ShieldCheck, t: 'Verified providers', b: 'HFZ-verified license & qualifications.' },
              { i: Star, t: 'Patient ratings', b: 'Transparent quality signals from previous patients.' },
              { i: Wifi, t: 'Live online status', b: 'See who is available right now for a consultation.' },
              { i: Calendar, t: 'Same-day booking', b: 'Book within minutes — no phone calls required.' },
            ].map((c, i) => (<motion.div key={c.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}><BulletCard icon={c.i} title={c.t} body={c.b} accent={TEAL} /></motion.div>))}
          </div>
        </div>
      </SlideShell>
    ),
  },
  // 6 — Consultation & Booking
  {
    key: 'booking', title: '05 · Consultation & Booking',
    render: () => (
      <SlideShell badge="Booking flow" title="Structured care from the first click" subtitle="Symptoms are captured before the consultation begins — saving time for both sides.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <Phone src={PHONE_IMAGES[3]} />
          <div className="space-y-3">
            <h4 className="text-base font-bold" style={{ color: TEAL_DARK }}>Doctor profile</h4>
            <div className="grid grid-cols-2 gap-2">
              {['Verified profile', 'Years of experience', 'Patient reviews', 'Transparent pricing'].map(x => (
                <div key={x} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg" style={{ background: 'white', border: '1px solid #e2e8f0', color: '#0f172a' }}><CheckCircle size={11} style={{ color: TEAL }} /> {x}</div>
              ))}
            </div>
            <h4 className="text-base font-bold mt-4" style={{ color: TEAL_DARK }}>Booking</h4>
            <div className="grid grid-cols-2 gap-2">
              {['Symptom collection', 'Consultation length', 'Time slot booking', 'Auto pricing'].map(x => (
                <div key={x} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg" style={{ background: 'white', border: '1px solid #e2e8f0', color: '#0f172a' }}><CheckCircle size={11} style={{ color: TEAL }} /> {x}</div>
              ))}
            </div>
          </div>
        </div>
      </SlideShell>
    ),
  },
  // 7 — Payments
  {
    key: 'payments', title: '06 · Payments — Built for Zambia',
    render: () => (
      <SlideShell badge="Mobile money native" title="Local payments, instant settlements" subtitle="Care365 supports every payment method Zambians actually use today.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: 'Airtel Money', col: '#dc2626', desc: 'Direct mobile money payments via Airtel' },
            { name: 'MTN Money', col: '#f59e0b', desc: 'Direct mobile money payments via MTN' },
            { name: 'Zamtel Kwacha', col: '#16a34a', desc: 'Direct mobile money payments via Zamtel' },
          ].map((p, i) => (
            <motion.div key={p.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="p-6 rounded-2xl bg-white shadow-sm" style={{ borderTop: `4px solid ${p.col}` }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: `${p.col}20` }}><Smartphone size={22} style={{ color: p.col }} /></div>
              <h4 className="text-lg font-bold mb-1" style={{ color: '#0f172a' }}>{p.name}</h4>
              <p className="text-xs" style={{ color: '#475569' }}>{p.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-6 p-4 rounded-xl text-sm" style={{ background: `${TEAL}10`, color: TEAL_DARK, border: `1px solid ${TEAL}30` }}>
          <strong>Why it matters:</strong> No card requirements, no bank accounts needed. Patients pay with the phone they already use.
        </div>
      </SlideShell>
    ),
  },
  // 8 — After Consultation
  {
    key: 'after', title: '07 · After the Consultation',
    render: () => (
      <SlideShell badge="Continuity of care" title="The consultation does not end when the call ends" subtitle="Care365 keeps the relationship — and the records — alive.">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { i: Pill, t: 'Digital prescriptions', b: 'Sent to pharmacies electronically.' },
            { i: Calendar, t: 'Follow-up scheduling', b: 'Automatic suggestions for next visit.' },
            { i: FileText, t: 'Full medical history', b: 'Patient records preserved across visits.' },
            { i: Stethoscope, t: 'Specialist referrals', b: 'Direct digital referral to other providers.' },
            { i: Activity, t: 'Lab & imaging follow-up', b: 'Track lab results and imaging in one place.' },
            { i: Heart, t: 'Wellness reminders', b: 'Medication and check-in reminders.' },
          ].map((c, i) => (<motion.div key={c.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}><BulletCard icon={c.i} title={c.t} body={c.b} accent={TEAL} /></motion.div>))}
        </div>
      </SlideShell>
    ),
  },
  // 9 — Doctor perspective
  {
    key: 'doctor', title: '08 · The Doctor Perspective',
    render: () => (
      <SlideShell badge="For HFZ members" title="New revenue opportunities for healthcare providers" subtitle="Care365 turns idle hours into paid consultations — without leaving the clinic.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {[
            { i: Calendar, t: 'Receive bookings', b: 'Patients book directly into the doctor\'s schedule.' },
            { i: Clock, t: 'Flexible schedule control', b: 'Doctors decide when they are online.' },
            { i: Video, t: 'Video / audio / chat', b: 'Choose the right consultation channel per patient.' },
            { i: Pill, t: 'Digital prescriptions', b: 'Issue prescriptions with one click.' },
            { i: FileText, t: 'Document the visit', b: 'Structured notes saved automatically.' },
            { i: DollarSign, t: 'Get paid quickly', b: 'Settlements via Mobile Money or bank.' },
          ].map((c, i) => (<motion.div key={c.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}><BulletCard icon={c.i} title={c.t} body={c.b} accent={TEAL} /></motion.div>))}
        </div>
        <div className="p-4 rounded-xl flex items-center gap-3" style={{ background: '#0f766e', color: 'white' }}>
          <Award size={20} />
          <p className="text-sm"><strong>~3,000 unemployed doctors in Zambia</strong> — Care365 unlocks consultation revenue without requiring relocation or capital investment.</p>
        </div>
      </SlideShell>
    ),
  },
  // 10 — HFZ Benefits
  {
    key: 'hfz', title: '09 · Benefits for HFZ',
    render: () => (
      <SlideShell badge="Why partner with Care365" title="Benefits for HFZ members" subtitle="A platform that strengthens the federation and every clinic in it.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { i: Globe, t: 'Reach patients nationwide', b: 'From Lusaka to Northern Province in one app.' },
            { i: DollarSign, t: 'Additional consultation revenue', b: 'New income stream per provider.' },
            { i: Smartphone, t: 'Digital presence without capex', b: 'No software project required.' },
            { i: TrendingUp, t: 'Reduced admin workload', b: 'Bookings, payments and records automated.' },
            { i: Heart, t: 'Better patient retention', b: 'Follow-ups keep patients in the system.' },
            { i: MapPin, t: 'Rural outreach capability', b: 'Serve communities without new clinics.' },
            { i: Activity, t: 'Centralised reporting', b: 'Dashboards & analytics across HFZ members.' },
            { i: ShieldCheck, t: 'Trusted by federation', b: 'HFZ remains the verification authority.' },
          ].map((c, i) => (<motion.div key={c.t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}><BulletCard icon={c.i} title={c.t} body={c.b} accent={TEAL} /></motion.div>))}
        </div>
      </SlideShell>
    ),
  },
  // 11 — Partnership Models
  {
    key: 'models', title: '10 · Partnership Models',
    render: () => (
      <SlideShell badge="Let's design this together" title="Three ways HFZ can partner with Care365" subtitle="Pick the model that fits the federation's ambition — or combine them.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { letter: 'A', title: 'Federation Partner', desc: 'HFZ recommends Care365 to its members. Lowest commitment, fastest start.', color: TEAL, points: ['Light-touch partnership', 'No exclusivity', 'Members opt-in individually'] },
            { letter: 'B', title: 'Federation Marketplace', desc: 'All member clinics join a shared HFZ-branded marketplace on Care365.', color: '#0891b2', points: ['HFZ co-branded experience', 'Centralised onboarding', 'Shared analytics for HFZ'] },
            { letter: 'C', title: 'National Telemedicine Program', desc: 'Joint initiative to expand healthcare access across Zambia.', color: '#7c3aed', points: ['Strategic joint venture', 'Government & donor alignment', 'Largest scale + impact'] },
          ].map((m, i) => (
            <motion.div key={m.letter} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="p-6 rounded-2xl bg-white shadow-sm flex flex-col" style={{ borderTop: `4px solid ${m.color}` }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl mb-3" style={{ background: `${m.color}20`, color: m.color }}>{m.letter}</div>
              <h4 className="text-base font-bold mb-1" style={{ color: '#0f172a' }}>Model {m.letter} — {m.title}</h4>
              <p className="text-xs mb-3" style={{ color: '#475569' }}>{m.desc}</p>
              <ul className="text-xs space-y-1 mt-auto">
                {m.points.map(p => <li key={p} className="flex items-start gap-1.5" style={{ color: '#0f172a' }}><CheckCircle size={11} style={{ color: m.color }} className="mt-0.5 flex-shrink-0" /> {p}</li>)}
              </ul>
            </motion.div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  // 12 — Vision
  {
    key: 'vision', title: '11 · The Vision',
    render: () => (
      <SlideShell theme="dark" noPadding>
        <div className="w-full h-full flex flex-col items-center justify-center p-8 md:p-12 relative overflow-hidden" style={{ background: `radial-gradient(circle at 50% 30%, ${TEAL}30 0%, ${BG_DARK} 70%)` }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center mb-10 max-w-3xl">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3" style={{ background: 'rgba(13,148,136,0.25)', color: '#5eead4' }}>The road ahead</div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">Building Zambia's Leading Digital Healthcare Ecosystem</h2>
            <p className="text-lg" style={{ color: '#94a3b8' }}>From patient to pharmacy, from clinic to insurer — one connected platform.</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl w-full">
            {[
              { i: Users, t: 'Patients' }, { i: Building2, t: 'Clinics' }, { i: Heart, t: 'Hospitals' }, { i: Stethoscope, t: 'Specialists' },
              { i: ShieldCheck, t: 'Insurance' }, { i: Award, t: 'NHIMA' }, { i: Pill, t: 'Pharmacies' }, { i: Activity, t: 'HFZ Network' },
            ].map((p, i) => (
              <motion.div key={p.t} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: 'rgba(13,148,136,0.25)' }}>
                  <p.i size={18} style={{ color: '#5eead4' }} />
                </div>
                <div className="text-xs font-bold text-white">{p.t}</div>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 text-sm font-bold tracking-widest uppercase" style={{ color: '#5eead4' }}>VoxIT · Innovage · HFZ — Better together.</p>
        </div>
      </SlideShell>
    ),
  },
];

/* ──────────────── Presentation shell ──────────────── */
export const Care365Presentation = ({ onClose }) => {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const next = () => setIdx(i => Math.min(slides.length - 1, i + 1));
  const prev = () => setIdx(i => Math.max(0, i - 1));

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); prev(); }
      else if (e.key === 'Escape') onClose?.();
      else if (e.key === 'f' || e.key === 'F') setFullscreen(f => !f);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const current = slides[idx];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.9)' }} data-testid="care365-presentation">
      <div className={`${fullscreen ? 'inset-0' : 'inset-4 md:inset-8'} absolute bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col`}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b flex-shrink-0" style={{ background: BG_DARK, borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="flex items-center gap-2 text-white">
            <Heart size={14} style={{ color: '#5eead4' }} /> <span className="text-xs font-bold">Care365 · Interactive Presentation</span>
          </div>
          <div className="flex items-center gap-2 text-white/80 text-xs">
            <span className="tabular-nums">{idx + 1} / {slides.length}</span>
            <button onClick={() => setFullscreen(f => !f)} className="p-1.5 rounded hover:bg-white/10" title="Toggle fullscreen (F)" data-testid="presentation-fullscreen">{fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}</button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10" title="Close (Esc)" data-testid="presentation-close"><X size={13} /></button>
          </div>
        </div>

        {/* Slide */}
        <div className="flex-1 relative min-h-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
              className="absolute inset-0"
            >
              {current.render()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom bar with slide nav */}
        <div className="flex items-center gap-2 px-4 py-2 border-t flex-shrink-0" style={{ background: BG_DARK, borderColor: 'rgba(255,255,255,0.1)' }}>
          <button onClick={prev} disabled={idx === 0} className="p-1.5 rounded text-white disabled:opacity-30 hover:bg-white/10" data-testid="presentation-prev"><ChevronLeft size={16} /></button>
          <div className="flex-1 flex items-center gap-1 overflow-x-auto">
            {slides.map((s, i) => (
              <button key={s.key} onClick={() => setIdx(i)} data-testid={`presentation-jump-${i}`} className="flex-shrink-0 transition-all" style={{ height: 4, width: i === idx ? 28 : 12, background: i === idx ? '#5eead4' : i < idx ? TEAL : 'rgba(255,255,255,0.2)', borderRadius: 999 }} title={s.title} />
            ))}
          </div>
          <span className="text-[10px] font-semibold text-white/60 hidden md:block min-w-0 truncate max-w-[200px]" title={current.title}>{current.title}</span>
          <button onClick={next} disabled={idx === slides.length - 1} className="p-1.5 rounded text-white disabled:opacity-30 hover:bg-white/10" data-testid="presentation-next"><ChevronRight size={16} /></button>
        </div>
      </div>
    </div>
  );
};
